import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { Sponsor } from "@event-planner/shared";
import { FormPageLayout } from "../../components/forms/FormPageLayout";
import { FormSection } from "../../components/forms/FormSection";
import { SchemaField, type FieldConfig } from "../../components/forms/SchemaField";
import { ImageUpload } from "../../components/uploads/ImageUpload";
import { useCreateSponsor, useUpdateSponsor } from "../../hooks/useSponsorMutations";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { sponsorSchema, type SponsorFormData } from "../../schemas/sponsorSchema";

const TIER_OPTIONS = [
  { value: "platinum", label: "Platinum" },
  { value: "gold", label: "Gold" },
  { value: "silver", label: "Silver" },
  { value: "bronze", label: "Bronze" },
  { value: "custom", label: "Custom" },
];

function buildDefaultValues(initialData?: Sponsor): SponsorFormData {
  if (initialData) {
    return {
      name: initialData.name,
      tier: initialData.tier,
      description: initialData.description,
      websiteURL: initialData.websiteURL,
      contactEmail: initialData.contactEmail,
      contactPhone: initialData.contactPhone,
      logoURL: initialData.logoURL,
      showOnEventHome: initialData.showOnEventHome,
      sortOrder: initialData.sortOrder,
    };
  }

  return {
    name: "",
    tier: "gold",
    description: null,
    websiteURL: null,
    contactEmail: null,
    contactPhone: null,
    logoURL: null,
    showOnEventHome: false,
    sortOrder: 0,
  };
}

const BASIC_FIELDS: FieldConfig<SponsorFormData>[] = [
  { name: "name", label: "Sponsor Name", type: "text", required: true },
  {
    name: "tier",
    label: "Tier",
    type: "select",
    required: true,
    options: TIER_OPTIONS,
  },
  { name: "description", label: "Description", type: "textarea", rows: 3 },
];

const CONTACT_FIELDS: FieldConfig<SponsorFormData>[] = [
  {
    name: "websiteURL",
    label: "Website URL",
    type: "text",
    placeholder: "https://...",
  },
  { name: "contactEmail", label: "Contact Email", type: "text" },
  { name: "contactPhone", label: "Contact Phone", type: "text" },
];

const DISPLAY_FIELDS: FieldConfig<SponsorFormData>[] = [
  {
    name: "showOnEventHome",
    label: "Show on event home screen",
    type: "switch",
  },
  {
    name: "sortOrder",
    label: "Sort Order",
    type: "number",
    helperText: "Controls display order within the same tier (lower = first)",
  },
];

interface SponsorFormProps {
  mode: "create" | "edit";
  initialData?: Sponsor;
}

export function SponsorForm({ mode, initialData }: SponsorFormProps) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { create, loading: creating } = useCreateSponsor();
  const { update, loading: updating } = useUpdateSponsor();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<SponsorFormData>({
    resolver: zodResolver(sponsorSchema),
    defaultValues: buildDefaultValues(initialData),
  });

  const submitting = creating || updating;

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    form.formState.isDirty,
    form.formState.isSubmitting || submitting,
  );

  async function onSubmit(data: SponsorFormData) {
    setServerError(null);

    // Normalize empty strings to null for optional URL/email fields
    const normalized = {
      ...data,
      websiteURL: data.websiteURL || null,
      contactEmail: data.contactEmail || null,
      contactPhone: data.contactPhone || null,
    };

    try {
      if (mode === "create") {
        await create({
          eventId: eventId!,
          ...normalized,
        });
        showSuccess("Sponsor created");
      } else {
        await update(initialData!.id, normalized);
        showSuccess("Sponsor updated");
      }
      navigate(`/events/${eventId}/sponsors`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save sponsor";
      setServerError(message);
      showError(message);
    }
  }

  return (
    <>
      {unsavedGuardDialog}
      <FormPageLayout
        title={mode === "create" ? "Add Sponsor" : "Edit Sponsor"}
      onSubmit={form.handleSubmit(onSubmit)}
      error={serverError}
      submitting={submitting}
      submitLabel={mode === "create" ? "Add Sponsor" : "Save Changes"}
    >
      <FormSection title="Basic Info">
        {BASIC_FIELDS.map((field) => (
          <SchemaField key={field.name} config={field} control={form.control} />
        ))}
      </FormSection>

      <FormSection title="Contact">
        {CONTACT_FIELDS.map((field) => (
          <SchemaField key={field.name} config={field} control={form.control} />
        ))}
      </FormSection>

      <FormSection title="Display">
        {DISPLAY_FIELDS.map((field) => (
          <SchemaField key={field.name} config={field} control={form.control} />
        ))}
      </FormSection>

      {mode === "edit" && initialData && (
        <FormSection title="Logo">
          <Controller
            control={form.control}
            name="logoURL"
            render={({ field }) => (
              <ImageUpload
                value={field.value}
                onChange={field.onChange}
                preset="logo"
                storagePathPrefix={`events/${eventId}/sponsors/${initialData.id}/logo`}
                label="Sponsor logo"
              />
            )}
          />
        </FormSection>
      )}
      </FormPageLayout>
    </>
  );
}
